import React, { useState, useEffect } from 'react';
import { Profile } from '@/types/profile';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Heart, MessageCircle, MoreVertical, Flag, Shield, Crown, Circle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ProfilePhotoCarousel } from '@/components/profile/ProfilePhotoCarousel';
import { SimpleConnectionStatus } from '@/hooks/useSimpleConnectionStatus';
import { BlockUserButton } from '@/components/shared/BlockUserButton';
import { ReportUserModal } from '@/components/shared/ReportUserModal';
import { businessRulesEngine } from '@/core/BusinessRulesEngine';
import type { BadgeIcon, ProfileStatus } from '@/core/BusinessRulesEngine/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * OPÇÃO B: Card Flutuante Glassmorphism
 * - Card translúcido sobrepondo levemente a foto
 * - Efeito "cartão premium" flutuante
 * - Backdrop blur intenso
 */

interface ProfileHeaderOptionBProps {
  profile: Profile;
  connectionStatus: SimpleConnectionStatus;
  onAction: (actionType: 'like' | 'unlike' | 'message' | 'unmatch') => void;
  canLike?: boolean;
  likeLimits?: {
    current: number;
    limit: number;
    remaining: number;
    isUnlimited: boolean;
  };
}

const ProfileHeaderOptionB: React.FC<ProfileHeaderOptionBProps> = ({
  profile,
  connectionStatus,
  onAction,
  canLike = true,
  likeLimits
}) => {
  const navigate = useNavigate();
  const [showReportModal, setShowReportModal] = useState(false);
  const [profileStatus, setProfileStatus] = useState<ProfileStatus>({
    badges: [],
    borderStyle: {
      containerClass: 'border border-white/10',
      priority: 6,
      label: 'Padrão'
    }
  });

  useEffect(() => {
    businessRulesEngine.getProfileStatus(profile).then(setProfileStatus);
  }, [profile]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/main/feed');
    }
  };

  const allPhotos = React.useMemo(() => {
    const photoArray: string[] = [];
    if (profile.main_photo_url) {
      photoArray.push(profile.main_photo_url);
    }
    if (profile.profile_photos && profile.profile_photos.length > 0) {
      profile.profile_photos.forEach(photo => {
        if (photo !== profile.main_photo_url && !photoArray.includes(photo)) {
          photoArray.push(photo);
        }
      });
    }
    return photoArray;
  }, [profile.main_photo_url, profile.profile_photos]);

  const getIcon = (iconName: BadgeIcon) => {
    switch(iconName) {
      case 'Shield': return Shield;
      case 'Crown': return Crown;
      case 'Circle': return Circle;
      default: return Shield;
    }
  };

  const isLiked = connectionStatus === 'sent' || connectionStatus === 'matched';
  const handleLikeClick = () => {
    if (isLiked) {
      onAction('unlike');
    } else {
      onAction('like');
    }
  };

  const getLimitText = () => {
    if (!likeLimits || likeLimits.isUnlimited) return null;
    return `${likeLimits.remaining} curtidas restantes`;
  };

  return (
    <div>
      {/* Photo Carousel with Back Button */}
      <div className="relative">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={handleBack} 
          className="absolute top-4 left-4 z-50 bg-black/50 backdrop-blur-sm text-white hover:bg-white hover:text-black transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>

        {/* Photo Carousel */}
        <ProfilePhotoCarousel
          photos={allPhotos}
          userName={profile.username || 'Usuário'}
          isVerified={profile.verification_level === 'full'}
        />
      </div>

      {/* Floating Glassmorphism Card - BELOW carousel, full width */}
      <div className="bg-white/5 border border-white/10 rounded-b-2xl p-4">
        {/* User Info */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white">
              {profile.username || 'Usuário'}
            </h1>
            {profileStatus.badges.map((badge) => {
              const Icon = getIcon(badge.icon);
              return (
                <div
                  key={badge.type}
                  className={`${badge.bgColor} rounded-full p-1 border border-white/50 ${badge.animation || ''}`}
                  style={{ boxShadow: badge.glowEffect }}
                >
                  <Icon 
                    className={`h-3.5 w-3.5 ${badge.color} ${badge.icon === 'Shield' ? 'fill-current' : ''}`} 
                  />
                </div>
              );
            })}
          </div>
          
          {profile.id && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="h-8 w-8 text-white/70 hover:text-white hover:bg-white/10"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-gray-900 border-white/10">
                <DropdownMenuItem 
                  onClick={() => setShowReportModal(true)}
                  className="text-amber-400 focus:text-amber-400 focus:bg-amber-500/10 cursor-pointer"
                >
                  <Flag className="h-4 w-4 mr-2" />
                  Denunciar
                </DropdownMenuItem>
                <BlockUserButton 
                  userId={profile.id} 
                  username={profile.username} 
                  variant="menuItem" 
                />
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <p className="text-white/80 text-sm mb-4">
          {profile.age} anos • {profile.city}, {profile.state_code}
        </p>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            onClick={() => onAction('message')} 
            className="flex-1 bg-gradient-to-r from-[#C4406B] to-[#9F3764] hover:from-[#D4507B] hover:to-[#AF4774] text-white border-0"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            Conversar
          </Button>
          
          <Button 
            onClick={handleLikeClick}
            variant="outline" 
            size="icon"
            className="border-luxury-pinkBright/50 text-luxury-pinkBright hover:bg-luxury-pinkBright/20 disabled:opacity-50"
            disabled={!canLike}
          >
            <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
          </Button>
        </div>

        {!canLike && getLimitText() && (
          <div className="text-xs text-white/50 text-center mt-2">
            {getLimitText()}
          </div>
        )}
      </div>

      {/* Report Modal */}
      {profile.id && (
        <ReportUserModal 
          userId={profile.id} 
          username={profile.username}
          open={showReportModal}
          onOpenChange={setShowReportModal}
        />
      )}
    </div>
  );
};

export default ProfileHeaderOptionB;
